import { unwrap, type Schemas } from '@swarm/api-client';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export type HoneyVariety = Schemas['HoneyVarietyDto'];
export type Container = Schemas['ContainerDto'];
export type Article = Schemas['ArticleDto'];
export type BatchListItem = Schemas['BatchListItemDto'];
export type Batch = Schemas['BatchDto'];
export type SaveBatchRequest = Schemas['SaveBatchRequest'];
export type Filling = Schemas['FillingDto'];
export type Movement = Schemas['MovementDto'];
export type MovementKind = Schemas['MovementKindDto'];
export type CorrectionReason = Schemas['CorrectionReasonDto'];
export type RecordFillingRequest = Schemas['RecordFillingRequest'];
export type RecordCorrectionRequest = Schemas['RecordCorrectionRequest'];
export type StockOverview = Schemas['StockOverviewDto'];
export type ColonyYield = Schemas['ColonyYieldDto'];
export type LocationYield = Schemas['LocationYieldDto'];
export type YieldYear = Schemas['YieldYearDto'];

export interface BatchFilter {
  year?: number | undefined;
  variety_id?: string | undefined;
}

const keys = {
  all: ['harvest'] as const,
  varieties: (archived: boolean) => ['harvest', 'varieties', archived] as const,
  containers: (archived: boolean) => ['harvest', 'containers', archived] as const,
  articles: (archived: boolean) => ['harvest', 'articles', archived] as const,
  batches: (f: BatchFilter) => ['harvest', 'batches', f] as const,
  batch: (id: string) => ['harvest', 'batch', id] as const,
  stock: ['harvest', 'stock'] as const,
  colonyYield: (id: string) => ['harvest', 'colony-yield', id] as const,
  locationYield: (id: string) => ['harvest', 'location-yield', id] as const,
};

/** Harvest changes affect stock, yields and colony timelines. */
function invalidate(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.all }),
    client.invalidateQueries({ queryKey: ['apiary'] }),
  ]);
}

const ifMatch = (version?: number): Record<string, string> =>
  version === undefined ? {} : { 'If-Match': `"${version}"` };

// --- Reference data -------------------------------------------------------------

export function useVarieties(includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: keys.varieties(includeArchived),
    queryFn: async () =>
      unwrap(
        await api.GET('/honey-varieties', {
          params: { query: { include_archived: includeArchived } },
        }),
      ),
    enabled,
  });
}

export function useSaveVariety() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      version,
    }: {
      id?: string | undefined;
      name: string;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/honey-varieties/{variety_id}', {
              params: { path: { variety_id: id } },
              body: { name },
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/honey-varieties', { body: { name } })),
    onSuccess: () => invalidate(client),
  });
}

export function useArchiveVariety() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      unwrap(
        await api.PUT('/honey-varieties/{variety_id}/archived', {
          params: { path: { variety_id: id } },
          body: { archived },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useContainers(includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: keys.containers(includeArchived),
    queryFn: async () =>
      unwrap(
        await api.GET('/containers', { params: { query: { include_archived: includeArchived } } }),
      ),
    enabled,
  });
}

export function useSaveContainer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id?: string | undefined;
      body: Schemas['SaveContainerRequest'];
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/containers/{container_id}', {
              params: { path: { container_id: id } },
              body,
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/containers', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useArchiveContainer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      unwrap(
        await api.PUT('/containers/{container_id}/archived', {
          params: { path: { container_id: id } },
          body: { archived },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useArticles(includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: keys.articles(includeArchived),
    queryFn: async () =>
      unwrap(
        await api.GET('/articles', { params: { query: { include_archived: includeArchived } } }),
      ),
    enabled,
  });
}

export function useCreateArticle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas['CreateArticleRequest']) =>
      unwrap(await api.POST('/articles', { body })),
    onSuccess: () => invalidate(client),
  });
}

export function useRenameArticle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name, version }: { id: string; name: string; version: number }) =>
      unwrap(
        await api.PUT('/articles/{article_id}', {
          params: { path: { article_id: id } },
          body: { name },
          headers: ifMatch(version),
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useArchiveArticle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      unwrap(
        await api.PUT('/articles/{article_id}/archived', {
          params: { path: { article_id: id } },
          body: { archived },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

// --- Batches ----------------------------------------------------------------------

export function useBatches(filter: BatchFilter = {}, enabled = true) {
  return useQuery({
    queryKey: keys.batches(filter),
    queryFn: async () =>
      unwrap(
        await api.GET('/batches', {
          params: {
            query: {
              ...(filter.year ? { year: filter.year } : {}),
              ...(filter.variety_id ? { variety_id: filter.variety_id } : {}),
            },
          },
        }),
      ),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useBatch(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.batch(id),
    queryFn: async () =>
      unwrap(await api.GET('/batches/{batch_id}', { params: { path: { batch_id: id } } })),
    enabled: enabled && id !== '',
  });
}

export function useSaveBatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      lot,
      version,
    }: {
      id?: string | undefined;
      body: SaveBatchRequest;
      lot?: string | undefined;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/batches/{batch_id}', {
              params: { path: { batch_id: id } },
              body,
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/batches', { body: { ...body, lot: lot || null } })),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteBatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.DELETE('/batches/{batch_id}', { params: { path: { batch_id: id } } })),
    onSuccess: () => invalidate(client),
  });
}

export function useRecordFilling() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ batchId, body }: { batchId: string; body: RecordFillingRequest }) =>
      unwrap(
        await api.POST('/batches/{batch_id}/fillings', {
          params: { path: { batch_id: batchId } },
          body,
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useCancelFilling() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ batchId, fillingId }: { batchId: string; fillingId: string }) =>
      unwrap(
        await api.POST('/batches/{batch_id}/fillings/{filling_id}/cancel', {
          params: { path: { batch_id: batchId, filling_id: fillingId } },
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useRecordCorrection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ batchId, body }: { batchId: string; body: RecordCorrectionRequest }) =>
      unwrap(
        await api.POST('/batches/{batch_id}/corrections', {
          params: { path: { batch_id: batchId } },
          body,
        }),
      ),
    onSuccess: () => invalidate(client),
  });
}

// --- Stock and yield --------------------------------------------------------------

export function useStock(enabled = true) {
  return useQuery({
    queryKey: keys.stock,
    queryFn: async () => unwrap(await api.GET('/stock')),
    enabled,
  });
}

export function useColonyYield(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.colonyYield(id),
    queryFn: async () =>
      unwrap(await api.GET('/colonies/{colony_id}/yield', { params: { path: { colony_id: id } } })),
    enabled,
  });
}

export function useLocationYield(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.locationYield(id),
    queryFn: async () =>
      unwrap(
        await api.GET('/locations/{location_id}/yield', {
          params: { path: { location_id: id } },
        }),
      ),
    enabled,
  });
}
